import { type CSSProperties } from "react";
import { ShimmerButton } from "@aisquare/ui";
import { RocketIcon, SparklesIcon } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Pin --primary to the brand navy (+ a light foreground) so the default fill
 * reads as rich navy in BOTH themes — the lib's shadcn --primary inverts to
 * near-white under .dark, which would wash the hero CTA out. Matches Button.demo
 * (handoff §1c). `display:contents` scopes the var without changing layout.
 */
const NAVY_PRIMARY = {
  "--primary": "212 72% 16%",
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

const Pinned = ({ children }: { children: React.ReactNode }) => (
  <div className="contents" style={NAVY_PRIMARY}>
    {children}
  </div>
);

export function ShimmerButtonDemo() {
  return (
    <Section
      id="shimmer-button"
      title="ShimmerButton"
      description="Hero CTA with an orbiting conic spark. Fill + spark default to --primary / --primary-foreground. The spark is preserved untouched; in the Onyx 'offset key' language it takes the quiet press tier — a 1px press-DOWN on the shared springy bounce (no notch / block: it's a pill by design and the spark mask rides its radius). One per surface — reach for Button for everyday actions."
    >
      <VariantBlock label="Default (primary)" caption="press-DOWN 1px + springy bounce">
        <Pinned>
          <ShimmerButton>Publish my studio</ShimmerButton>
          <ShimmerButton>
            <RocketIcon className="size-4" />
            Launch
          </ShimmerButton>
        </Pinned>
      </VariantBlock>

      <VariantBlock label="Accent fill" caption='background="hsl(var(--accent))"'>
        <ShimmerButton background="hsl(var(--accent))" shimmerColor="hsl(var(--accent-foreground))">
          <SparklesIcon className="size-4" />
          Upgrade to Pro
        </ShimmerButton>
      </VariantBlock>

      <VariantBlock label="Tuning" caption="shimmerDuration / borderRadius">
        <Pinned>
          <ShimmerButton shimmerDuration="1.5s">Faster spark</ShimmerButton>
          <ShimmerButton borderRadius="12px">Squared</ShimmerButton>
        </Pinned>
      </VariantBlock>

      <VariantBlock label="States">
        <Pinned>
          <ShimmerButton disabled>Disabled</ShimmerButton>
        </Pinned>
      </VariantBlock>
    </Section>
  );
}
